import React, { useState, useEffect } from 'react';
import axios from 'axios';

const VowsForm = ({ onGenerate, code, onSuccess, onError, loading: externalLoading, initialFormData, isRetry, existingVowId }) => {
  const [formData, setFormData] = useState({
    yourName: initialFormData?.yourName || '',
    partnerName: initialFormData?.partnerName || '',
    weddingDate: initialFormData?.weddingDate || '',
    relationshipLength: initialFormData?.relationshipLength || '',
    howYouMet: initialFormData?.howYouMet || '',
    firstImpression: initialFormData?.firstImpression || '',
    favoriteMemory: initialFormData?.favoriteMemory || '',
    whatYouLove: initialFormData?.whatYouLove || '',
    sharedInterests: initialFormData?.sharedInterests || '',
    insideJokes: initialFormData?.insideJokes || '',
    challenges: initialFormData?.challenges || '',
    futureHopes: initialFormData?.futureHopes || '',
    promises: initialFormData?.promises || '',
    tone: initialFormData?.tone || 'romantic',
    includeHumor: initialFormData?.includeHumor || 'no',
    religiousElements: initialFormData?.religiousElements || 'no',
    specialQuote: initialFormData?.specialQuote || '',
    additionalNotes: initialFormData?.additionalNotes || ''
  });

  useEffect(() => {
    if (initialFormData) {
      setFormData(prev => ({
        ...prev,
        ...initialFormData
      }));
    }
  }, [initialFormData]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Generate vows text from form data (for API submission)
  const generateVowsText = () => {
    const parts = [];
    
    parts.push(`Wedding Vows for ${formData.partnerName}`);
    parts.push(`From ${formData.yourName}`);
    if (formData.weddingDate) {
      parts.push(`Wedding Date: ${new Date(formData.weddingDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`);
    }
    
    if (formData.howYouMet) {
      parts.push(`\nHow We Met:\n${formData.howYouMet}`);
    }
    
    if (formData.firstImpression) {
      parts.push(`\nFirst Impression:\n${formData.firstImpression}`);
    }
    
    if (formData.favoriteMemory) {
      parts.push(`\nFavorite Memory:\n${formData.favoriteMemory}`);
    }
    
    if (formData.whatYouLove) {
      parts.push(`\nWhat I Love About You:\n${formData.whatYouLove}`);
    }
    
    if (formData.promises) {
      parts.push(`\nMy Promises:\n${formData.promises}`);
    }
    
    if (formData.futureHopes) {
      parts.push(`\nOur Future:\n${formData.futureHopes}`);
    }
    
    return parts.join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (code) {
      setLoading(true);
      try {
        let vowsText;
        
        try {
          const aiResponse = await axios.post('/api/generate-vows', {
            formData: formData
          });
          vowsText = aiResponse.data.vows_text;
        } catch (aiError) {
          console.log('AI generation failed, using fallback:', aiError.response?.data?.error || aiError.message);
          vowsText = generateVowsText();
        }
        
        const vowsData = {
          your_name: formData.yourName,
          partner_name: formData.partnerName,
          wedding_date: formData.weddingDate || null,
          full_text: vowsText,
          form_data: formData
        };

        const response = await axios.post('/api/save-vows', {
          code: code.toUpperCase(),
          vowsData,
          existingVowId: isRetry ? existingVowId : null
        });
        
        if (onSuccess) {
          onSuccess(response.data.vow);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.error || 'Failed to generate vows. Please try again.';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(true);
      try {
        let vowsText;
        
        try {
          const aiResponse = await axios.post('/api/generate-vows', {
            formData: formData
          });
          vowsText = aiResponse.data.vows_text;
        } catch (aiError) {
          console.log('AI generation failed, using fallback:', aiError.response?.data?.error || aiError.message);
          vowsText = generateVowsText();
        }
        
        if (onGenerate) {
          onGenerate({
            ...formData,
            vowsText
          });
        }
      } catch (err) {
        const errorMessage = err.response?.data?.error || 'Failed to generate vows. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="glass-card">
      <h2 className="mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Tell Us Your Love Story</h2>
      <form onSubmit={handleSubmit}>
        {/* Names Section */}
        <div className="row mb-3">
          <div className="col-md-6">
            <label htmlFor="yourName" className="form-label">Your Name *</label>
            <input
              type="text"
              className="form-control"
              id="yourName"
              name="yourName"
              value={formData.yourName}
              onChange={handleChange}
              placeholder="Your first name"
              required
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="partnerName" className="form-label">Partner's Name *</label>
            <input
              type="text"
              className="form-control"
              id="partnerName"
              name="partnerName"
              value={formData.partnerName}
              onChange={handleChange}
              placeholder="Partner's first name"
              required
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <label htmlFor="weddingDate" className="form-label">Wedding Date</label>
            <input
              type="date"
              className="form-control"
              id="weddingDate"
              name="weddingDate"
              value={formData.weddingDate}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="relationshipLength" className="form-label">How Long Together?</label>
            <input
              type="text"
              className="form-control"
              id="relationshipLength"
              name="relationshipLength"
              value={formData.relationshipLength}
              onChange={handleChange}
              placeholder="e.g., 3 years, 5 years"
            />
          </div>
        </div>

        <hr className="my-4" style={{ borderColor: 'rgba(30, 58, 95, 0.3)' }} />
        <h3 className="mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          <i className="bi bi-heart me-2" style={{ color: '#1e3a5f' }}></i>
          Your Story
        </h3>

        <div className="mb-3">
          <label htmlFor="howYouMet" className="form-label">How Did You Meet? *</label>
          <textarea
            className="form-control"
            id="howYouMet"
            name="howYouMet"
            rows="3"
            value={formData.howYouMet}
            onChange={handleChange}
            placeholder="Describe the moment you first met... where, when, what happened"
            required
          />
        </div>

        <div className="mb-3">
          <label htmlFor="firstImpression" className="form-label">What Was Your First Impression?</label>
          <textarea
            className="form-control"
            id="firstImpression"
            name="firstImpression"
            rows="2"
            value={formData.firstImpression}
            onChange={handleChange}
            placeholder="What did you think or feel when you first met them?"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="favoriteMemory" className="form-label">Favorite Memory Together</label>
          <textarea
            className="form-control"
            id="favoriteMemory"
            name="favoriteMemory"
            rows="3"
            value={formData.favoriteMemory}
            onChange={handleChange}
            placeholder="Describe a special moment you've shared together"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="whatYouLove" className="form-label">What Do You Love Most About Them? *</label>
          <textarea
            className="form-control"
            id="whatYouLove"
            name="whatYouLove"
            rows="3"
            value={formData.whatYouLove}
            onChange={handleChange}
            placeholder="Their qualities, the way they make you feel, little things they do..."
            required
          />
        </div>

        <div className="mb-3">
          <label htmlFor="sharedInterests" className="form-label">Shared Interests & Activities</label>
          <textarea
            className="form-control"
            id="sharedInterests"
            name="sharedInterests"
            rows="2"
            value={formData.sharedInterests}
            onChange={handleChange}
            placeholder="Things you love doing together, hobbies, traditions..."
          />
        </div>

        <div className="mb-3">
          <label htmlFor="insideJokes" className="form-label">Inside Jokes or Pet Names (Optional)</label>
          <input
            type="text"
            className="form-control"
            id="insideJokes"
            name="insideJokes"
            value={formData.insideJokes}
            onChange={handleChange}
            placeholder="Any nicknames or special phrases just between you two"
          />
        </div>

        <hr className="my-4" style={{ borderColor: 'rgba(30, 58, 95, 0.3)' }} />
        <h3 className="mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          <i className="bi bi-ring me-2" style={{ color: '#1e3a5f' }}></i>
          Promises & Future
        </h3>

        <div className="mb-3">
          <label htmlFor="challenges" className="form-label">Challenges You've Overcome Together</label>
          <textarea
            className="form-control"
            id="challenges"
            name="challenges"
            rows="2"
            value={formData.challenges}
            onChange={handleChange}
            placeholder="Any obstacles you've faced and overcome as a couple"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="promises" className="form-label">Specific Promises You Want to Make *</label>
          <textarea
            className="form-control"
            id="promises"
            name="promises"
            rows="3"
            value={formData.promises}
            onChange={handleChange}
            placeholder="e.g., I promise to always support your dreams, to make you laugh daily, to never go to bed angry..."
            required
          />
        </div>

        <div className="mb-3">
          <label htmlFor="futureHopes" className="form-label">Your Hopes for the Future</label>
          <textarea
            className="form-control"
            id="futureHopes"
            name="futureHopes"
            rows="3"
            value={formData.futureHopes}
            onChange={handleChange}
            placeholder="What you look forward to in your life together, dreams, goals..."
          />
        </div>

        <hr className="my-4" style={{ borderColor: 'rgba(30, 58, 95, 0.3)' }} />
        <h3 className="mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          <i className="bi bi-sliders me-2" style={{ color: '#1e3a5f' }}></i>
          Style Preferences
        </h3>

        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="tone" className="form-label">Tone</label>
            <select
              className="form-select"
              id="tone"
              name="tone"
              value={formData.tone}
              onChange={handleChange}
            >
              <option value="romantic">Romantic & Poetic</option>
              <option value="heartfelt">Heartfelt & Sincere</option>
              <option value="traditional">Traditional & Classic</option>
              <option value="modern">Modern & Contemporary</option>
              <option value="playful">Playful & Fun</option>
            </select>
          </div>
          <div className="col-md-4">
            <label htmlFor="includeHumor" className="form-label">Include Humor?</label>
            <select
              className="form-select"
              id="includeHumor"
              name="includeHumor"
              value={formData.includeHumor}
              onChange={handleChange}
            >
              <option value="no">No, keep it serious</option>
              <option value="light">Light touches of humor</option>
              <option value="yes">Yes, we love to laugh</option>
            </select>
          </div>
          <div className="col-md-4">
            <label htmlFor="religiousElements" className="form-label">Religious Elements?</label>
            <select
              className="form-select"
              id="religiousElements"
              name="religiousElements"
              value={formData.religiousElements}
              onChange={handleChange}
            >
              <option value="no">No religious references</option>
              <option value="subtle">Subtle spiritual tone</option>
              <option value="christian">Christian</option>
              <option value="jewish">Jewish</option>
              <option value="other">Other (specify in notes)</option>
            </select>
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="specialQuote" className="form-label">Special Quote or Song Lyric</label>
          <input
            type="text"
            className="form-control"
            id="specialQuote"
            name="specialQuote"
            value={formData.specialQuote}
            onChange={handleChange}
            placeholder="A quote or lyric that's meaningful to your relationship"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="additionalNotes" className="form-label">Additional Notes</label>
          <textarea
            className="form-control"
            id="additionalNotes"
            name="additionalNotes"
            rows="3"
            value={formData.additionalNotes}
            onChange={handleChange}
            placeholder="Anything else you'd like us to include or consider"
          />
        </div>

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        )}

        <div className="text-center">
          <button 
            type="submit" 
            className="btn btn-primary btn-lg w-100"
            disabled={loading || externalLoading}
          >
            {loading || externalLoading ? 'Creating Your Vows...' : (code ? 'Generate Vows & PDF' : 'Generate Vows')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VowsForm;

